/**
 * 强密码生成工具
 * 生成符合安全要求的复杂密码
 */

export interface PasswordOptions {
  length?: number;
  includeUppercase?: boolean;
  includeLowercase?: boolean;
  includeNumbers?: boolean;
  includeSymbols?: boolean;
  excludeSimilar?: boolean; // 排除容易混淆的字符
}

const DEFAULT_OPTIONS: Required<PasswordOptions> = {
  length: 16,
  includeUppercase: true,
  includeLowercase: true,
  includeNumbers: true,
  includeSymbols: true,
  excludeSimilar: true,
};

// 字符集定义
const UPPERCASE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWERCASE = 'abcdefghijklmnopqrstuvwxyz';
const NUMBERS = '0123456789';
const SYMBOLS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

// 容易混淆的字符
const SIMILAR_CHARS = 'il1Lo0O';

/**
 * 生成强密码
 */
export function generateStrongPassword(options: PasswordOptions = {}): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  let charset = '';
  let requiredChars = '';
  
  // 构建字符集
  if (opts.includeUppercase) {
    const chars = opts.excludeSimilar ? 
      UPPERCASE.split('').filter(c => !SIMILAR_CHARS.includes(c)).join('') : 
      UPPERCASE;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (opts.includeLowercase) {
    const chars = opts.excludeSimilar ? 
      LOWERCASE.split('').filter(c => !SIMILAR_CHARS.includes(c)).join('') : 
      LOWERCASE;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (opts.includeNumbers) {
    const chars = opts.excludeSimilar ? 
      NUMBERS.split('').filter(c => !SIMILAR_CHARS.includes(c)).join('') : 
      NUMBERS;
    charset += chars;
    requiredChars += chars[Math.floor(Math.random() * chars.length)];
  }
  
  if (opts.includeSymbols) {
    charset += SYMBOLS;
    requiredChars += SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
  }
  
  if (charset.length === 0) {
    throw new Error('至少需要选择一种字符类型');
  }
  
  // 生成密码
  let password = requiredChars;
  
  // 填充剩余长度
  for (let i = requiredChars.length; i < opts.length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)];
  }
  
  // 打乱字符顺序
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

/**
 * 检查密码强度
 */
export function checkPasswordStrength(password: string): {
  score: number; // 0-100
  level: 'weak' | 'fair' | 'good' | 'strong';
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;
  
  // 长度检查
  if (password.length >= 12) {
    score += 25;
  } else if (password.length >= 8) {
    score += 15;
    feedback.push('建议密码长度至少12位');
  } else {
    score += 5;
    feedback.push('密码太短，建议至少8位');
  }
  
  // 字符类型检查
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);
  
  const typeCount = [hasUpper, hasLower, hasNumber, hasSymbol].filter(Boolean).length;
  score += typeCount * 15;
  
  if (!hasUpper) feedback.push('缺少大写字母');
  if (!hasLower) feedback.push('缺少小写字母');
  if (!hasNumber) feedback.push('缺少数字');
  if (!hasSymbol) feedback.push('缺少特殊符号');
  
  // 复杂度检查
  const uniqueChars = new Set(password).size;
  if (uniqueChars / password.length > 0.7) {
    score += 20;
  } else {
    feedback.push('字符重复度较高');
  }
  
  // 常见模式检查
  if (!/(.)\1{2,}/.test(password)) {
    score += 10;
  } else {
    feedback.push('避免连续相同字符');
  }
  
  // 确定级别
  let level: 'weak' | 'fair' | 'good' | 'strong';
  if (score >= 80) level = 'strong';
  else if (score >= 60) level = 'good';
  else if (score >= 40) level = 'fair';
  else level = 'weak';
  
  return { score, level, feedback };
}

/**
 * 复制文本到剪贴板
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // 现代浏览器
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('复制到剪贴板失败:', error);
    return false;
  }
}

/**
 * 生成用于用户的安全密码（预设配置）
 * 确保生成的密码达到强密码标准
 */
export function generateUserPassword(): string {
  let password = '';
  let attempts = 0;
  const maxAttempts = 10;
  
  do {
    password = generateStrongPassword({
      length: 16,
      includeUppercase: true,
      includeLowercase: true,
      includeNumbers: true,
      includeSymbols: true,
      excludeSimilar: true,
    });
    
    attempts++;
    const strength = checkPasswordStrength(password);
    
    // 如果密码强度达到 "good" 或以上，则返回
    if (strength.level === 'good' || strength.level === 'strong') {
      return password;
    }
    
  } while (attempts < maxAttempts);
  
  // 如果多次尝试都没有生成强密码，返回最后一次生成的密码
  return password;
}